import { memo, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { Carousel, CarouselContent, CarouselItem } from "app/components/ui/Carousel/Carousel";
import { MerchCard } from "app/features/merchandising";
import Separator from "app/pages/.shared/Separator";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import AppGlobalsContext from "app/AppGlobalsContext";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useTemplateRenderToString } from "app/utils/hooks/useTemplateRender";
import { htmlSanitize } from "app/utils/sanitize";
import {
	buildHotelOnlySearchParamsForDestination,
	buildHotelOnlySearchTagPayload,
} from "app/utils/smartDPUtils";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import "./CurrentMerchCollection.scss";

const SHOW_SLIDES_NUMBER = {
	[RESOLUTION.MEDIUM]: 3.3,
	[RESOLUTION.SMALL]: 1.4,
};

const PAGE_PADDING = 16;
const CARD_GAP = 24;

const CAROUSEL_OPTIONS = { loop: false, skipSnaps: true };

const CurrentMerchCollection = ({ merchandisingList = [], destinations = [] }) => {
	const { resolution } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();
	const { formatMessage } = useIntl();
	const templateRenderToString = useTemplateRenderToString();
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const renderMerchCard = (merchandising, photo) => {
		const { code, label, lowestPrice, position, headerIndex } = merchandising;
		const {
			stamp = {},
			smartDP = {},
			introduction,
			enableRedirectionToHotelOnlyListingPage,
		} = merchandising.displayOptions || {};
		const destinationFilters = merchandising.destinationFilters || [];

		const destinationResort =
			enableRedirectionToHotelOnlyListingPage && destinationFilters.length > 0
				? destinations.find(
						destination => destination.code === Number(destinationFilters[0]?.id)
					)
				: undefined;

		const to = destinationResort
			? {
					pathname: "/hotelonly/listing",
					search: `?${buildHotelOnlySearchParamsForDestination(destinationResort.code)}`,
				}
			: { pathname: `/merch/${code}` };

		const showPrice = stamp.hideLowestPrice !== true && lowestPrice > 0;

		const handleClick = () => {
			if (destinationResort) {
				track("sdp_search_submit", buildHotelOnlySearchTagPayload(destinationResort.code));
			}
			track("merchandising_click", {
				code,
				merchCode: code,
				position: String(position),
				indexCarousel: String(headerIndex),
				merchandisings: merchandisingList.map(merch => ({
					code: merch.code,
					position: merch.position,
					label: merch.label,
					headerIndex: merch.headerIndex,
					startAt: merch.startAt,
					endAt: merch.endAt,
				})),
			});
		};

		return (
			<MerchCard
				key={code}
				className="current-merch-collection__card"
				to={to}
				title={htmlSanitize(
					templateRenderToString(smartDP.merchandisingTitle || label || "")
				)}
				subtitle={htmlSanitize(
					templateRenderToString(smartDP.merchandisingSubtitle || introduction || "")
				)}
				photo={photo}
				price={showPrice ? lowestPrice : undefined}
				showPriceDrop={showPrice && Boolean(stamp.showLabel)}
				onClick={handleClick}
				data-testid={`merch-card-${code}`}
			/>
		);
	};

	return (
		<div className="current-merch-collection">
			<Typography
				variant={TYPOGRAPHY_VARIANTS.XL3}
				variantMD={TYPOGRAPHY_VARIANTS.XL4}
				isBold
				className="current-merch-collection__title"
				component="h2"
			>
				<Separator design="middle" />
				<FormattedMessage id="current.merch.collection.title" />
			</Typography>

			{isMobile ? (
				<Carousel
					className="current-merch-collection__carousel"
					opts={CAROUSEL_OPTIONS}
					slidesPerView={SHOW_SLIDES_NUMBER[resolution]}
					gap={CARD_GAP}
					bleed={PAGE_PADDING}
					aria-label={formatMessage({ id: "current.merch.collection.title" })}
				>
					<CarouselContent
						className="current-merch-collection__track"
						data-single={merchandisingList.length === 1 || undefined}
					>
						{merchandisingList.map(merchandising => (
							<CarouselItem
								key={merchandising.code}
								className="current-merch-collection__slide"
							>
								{renderMerchCard(merchandising, merchandising.photoSmall)}
							</CarouselItem>
						))}
					</CarouselContent>
				</Carousel>
			) : (
				<div className="current-merch-collection__list">
					{merchandisingList.map(merchandising =>
						renderMerchCard(merchandising, merchandising.photoLarge)
					)}
				</div>
			)}
		</div>
	);
};

CurrentMerchCollection.propTypes = {
	merchandisingList: PropTypes.arrayOf(PropTypes.object),
	destinations: PropTypes.array,
};

export default memo(CurrentMerchCollection);
